import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import {
  disableNvgForLeg,
  enableNvgForLeg,
  FlightLogFormHelper,
  isFixedWingOrUnknown,
} from 'src/app/flight-logs/flight-log-form-helper';
import { AircraftHttpClient } from 'src/app/shared/flight-log-api/aircraft/aircraft-client.service';
import { WaypointDto } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { CrewMemberDto } from 'src/app/shared/flight-log-api/shared/shared-client.model';
import {
  getControls,
  getSelfWhenLengthChanges,
  getValueChanges,
} from 'src/util/angular/ng-forms-helpers';
import {
  combineValidtionErrorsChanges,
  field,
  toggleValidators,
} from 'src/util/angular/ng-forms-validation';
import { kendoDateTime24HourFormat } from 'src/util/dateLib';

@Component({
  selector: 'fl-flight-leg',
  templateUrl: './flight-leg.component.html',
  styleUrls: ['./flight-leg.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightLegComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public formHelper: FlightLogFormHelper;
  @Input()
  public entry: FormGroup;
  @Input()
  public fromWaypoint: FormGroup;
  @Input()
  public toWaypoint: FormGroup;
  @Input()
  public entryIndex: number;
  @Input()
  public flightCrew: CrewMemberDto[];
  @Output()
  public delete = new EventEmitter();
  @Output()
  public resetLegs = new EventEmitter();

  public showDeleteOverlay = false;
  public fromWaypoint$: Observable<WaypointDto>;

  public toWaypoint$: Observable<WaypointDto>;
  public refueledSelected$: Observable<boolean>;
  public _onDestroy$ = new Subject();
  public nvgSelected$: Observable<boolean>;
  public fuelEvents$: Observable<FormArray>;
  public hasExtraMileage$: Observable<boolean>;
  public legErrors$: Observable<string[]>;
  public waypointErrors$: Observable<string[]>;
  public isFixedWing$: Observable<boolean>;
  public dateTimeFormat = kendoDateTime24HourFormat;

  constructor(private _aircraft: AircraftHttpClient) {}

  get canDisplayLegActions() {
    return this.formHelper.flight.status !== 'Complete';
  }

  get isLastLeg() {
    return this.formHelper.isLastLogEntry(this.entryIndex);
  }

  get addActionText() {
    return this.isLastLeg ? 'Add' : 'Insert';
  }

  public ngOnInit() {
    // Define Observables
    this.hasExtraMileage$ = getValueChanges(
      // tslint:disable-next-line: no-non-null-assertion
      this.entry.get('entryDetail.attributes.hasExtraMileage')!,
    );
    // tslint:disable-next-line: no-non-null-assertion
    this.nvgSelected$ = getValueChanges(this.entry.get('entryDetail.attributes.nvg')!);
    this.fuelEvents$ = getSelfWhenLengthChanges(this.fuelEvents);
    // tslint:disable-next-line: no-non-null-assertion
    this.refueledSelected$ = getValueChanges(this.entry.get('entryDetail.attributes.refueled')!);
    const aircraftType$ = this.formHelper.aircraftId$.pipe(
      switchMap((id) => (id != null ? this._aircraft.getById(id) : of(null))),
      map((aircraft) => (aircraft != null ? aircraft.aircraftType : null)),
    );
    this.isFixedWing$ = aircraftType$.pipe(
      map((aircraftType) => isFixedWingOrUnknown(aircraftType)),
    );

    // Subscribe to specific waypoint errors we care about for this leg
    const relevantFromWaypointControls =
      this.entryIndex === 0
        ? getControls(this.fromWaypoint, ['departureTime', 'description'])
        : getControls(this.fromWaypoint, ['departureTime']);
    const relevantToWaypointControls = getControls(this.toWaypoint, ['arrivalTime', 'description']);
    this.waypointErrors$ = combineValidtionErrorsChanges([
      ...relevantFromWaypointControls,
      ...relevantToWaypointControls,
    ]);

    this.legErrors$ = combineValidtionErrorsChanges([
      this.entry,
      // tslint:disable-next-line: no-non-null-assertion
      this.entry.get('flightTime')!,
      // tslint:disable-next-line: no-non-null-assertion
      this.entry.get('entryDetail.maxAltitude')!,
      // tslint:disable-next-line: no-non-null-assertion
      this.entry.get('entryDetail.cabinTemperature')!,
    ]);

    // Define Observable Logic
    this.nvgSelected$.pipe(takeUntil(this._onDestroy$)).subscribe((nvgSelected) => {
      if (nvgSelected) {
        enableNvgForLeg(this.entry);
      } else {
        disableNvgForLeg(this.entry);
      }
    });

    this.refueledSelected$.pipe(takeUntil(this._onDestroy$)).subscribe((refueled) => {
      if (!refueled) {
        this.fuelEvents.controls = [];
      } else if (this.fuelEvents.length === 0) {
        this.formHelper.addFuelEvent(this.entry);
      }
    });
  }

  get fuelEvents() {
    return this.entry.get('entryDetail.fuelEvents') as FormArray;
  }

  public ngOnDestroy() {
    this._onDestroy$.complete();
  }

  get crewOnboard(): FormArray {
    return this.entry.get('entryDetail.crewOnboard') as FormArray;
  }

  public ngOnChanges() {
    if (this.fromWaypoint != null) {
      this.fromWaypoint$ = this.fromWaypoint.valueChanges.pipe(startWith(this.fromWaypoint.value));
    }
    if (this.toWaypoint != null) {
      this.toWaypoint$ = this.toWaypoint.valueChanges.pipe(startWith(this.toWaypoint.value));
    }
  }

  public onboard(flightCrewId: string) {
    this.formHelper.addCrewMemberToLeg(this.entry, flightCrewId);
  }

  public addFuelEvent() {
    this.formHelper.addFuelEvent(this.entry);
  }

  public disboard(flightCrewId: string) {
    this.formHelper.removeCrewMemberFromLeg(this.entry, flightCrewId);
  }

  public getCrewMemberById(flightCrewId: string) {
    return this.flightCrew.find((fc) => fc.id === flightCrewId);
  }

  public getCrewMemberAboard$(flightCrewId: string): Observable<FormGroup> {
    return this.crewOnboard.valueChanges.pipe(
      map(() => {
        // tslint:disable-next-line: no-non-null-assertion
        const crewMemberLegInfo = this.getCrewMemberAboard(flightCrewId)!;
        return crewMemberLegInfo;
      }),
      // tslint:disable-next-line: no-non-null-assertion
      startWith(this.getCrewMemberAboard(flightCrewId)!),
    );
  }

  public getCrewMemberAboard(flightCrewId: string): FormGroup | null {
    for (let i = 0; i < this.crewOnboard.length; i++) {
      const crewMemberLegInfo = this.crewOnboard.get([i]) as FormGroup;
      if (crewMemberLegInfo != null && crewMemberLegInfo.value.flightCrewId === flightCrewId) {
        return crewMemberLegInfo;
      }
    }
    return null;
  }

  public onDeleteMouseover() {
    this.showDeleteOverlay = true;
  }

  public onDeleteMouseleave() {
    this.showDeleteOverlay = false;
  }
}
